import { HttpStatusCode } from '@angular/common/http';
import { Component, ElementRef, ViewChild, effect, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink, UrlTree } from '@angular/router';
import { SheydaProgramCardComponent } from '@components/cards/sheyda-program-card/sheyda-program-card.component';
import { EpisodeAccessType, Program } from '@shared/graphql/api.generated';
import { GeneralService } from '@shared/services/general.service';
import { ProgramService } from '@shared/services/program.service';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';

@Component({
  selector: 'app-sheyda-row',
  imports: [RouterLink, MatIconModule, SheydaProgramCardComponent],
  templateUrl: './sheyda-row.component.html',
  styleUrls: ['./sheyda-row.component.scss'],
  providers: [ProgramService],
})
export class SheydaRowComponent {
  generalService = inject(GeneralService);
  private programService = inject(ProgramService);
  private hostRef = inject(ElementRef);

  @ViewChild('desktopSliderRef') desktopSliderRef!: ElementRef<HTMLElement>;
  RowTitle = input.required<string>();
  RowTitleLink = input<string | UrlTree>();
  Data = input<Program[]>();
  items = signal<Program[]>([]);
  ItemId = input<string>();
  desktopSlider = signal<KeenSliderInstance | undefined>(undefined);
  currentSlide = signal(0);
  dotHelper = signal<number[]>([]);
  showRow = signal(false);
  itemsPerView = signal(6);
  accessTypes = EpisodeAccessType;
  private readonly shouldRender = signal(false);
  private intersectionObserver: IntersectionObserver | null = null;

  constructor() {
    effect(() => {
      if (this.shouldRender()) {
        if (this.Data()?.length) {
          this.items.set(this.Data()!);
          this.handleSlider();
        } else {
          this.getPrograms();
        }
      }
    });
  }

  ngAfterViewInit() {
    if (this.generalService.isBrowserOnly) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.shouldRender.set(true);
        }
      });
      this.intersectionObserver.observe(this.hostRef.nativeElement);
    } else {
      this.shouldRender.set(true);
    }
  }

  private getPrograms() {
    this.programService.getTagPrograms(this.ItemId()!).subscribe({
      next: (res) => {
        if (res.status.statusCode === HttpStatusCode.Ok) {
          this.items.set(res.data);
          if (this.items().length) {
            this.handleSlider();
          }
        }
      },
    });
  }

  private handleSlider() {
    if (!this.generalService.isSmallScreen() && this.generalService.isBrowserOnly) {
      setTimeout(() => {
        this.desktopSlider.set(
          new KeenSlider(this.desktopSliderRef.nativeElement, {
            initial: this.currentSlide(),
            loop: false,
            rtl: true,
            renderMode: 'performance',
            drag: false,
            breakpoints: {
              '(max-width: 1920px)': {
                slides: {
                  perView: 5,
                  spacing: 20,
                },
              },
              '(max-width: 1440px)': {
                slides: {
                  perView: 5,
                  spacing: 8,
                },
              },
              '(max-width: 1200px)': {
                slides: {
                  perView: 4,
                  spacing: 12,
                },
              },
              '(max-width: 992px)': {
                slides: {
                  perView: 3,
                  spacing: 12,
                },
              },
            },
            slides: {
              perView: 6,
              spacing: 20,
            },
            slideChanged: (s) => {
              this.currentSlide.set(s.track.details.rel);
            },
            created: () => {
              this.showRow.set(true);
              const width = window.innerWidth;
              if (width <= 1920 && width > 1200) {
                this.itemsPerView.set(5);
              } else if (width <= 1200 && width > 992) {
                this.itemsPerView.set(4);
              } else if (width <= 992 && width > 768) {
                this.itemsPerView.set(3);
              }
            },
          }),
        );
      });
    } else {
      this.showRow.set(true);
    }
  }

  ngOnDestroy() {
    if (this.desktopSlider()) {
      this.desktopSlider()?.destroy();
    }
  }

  handleTitleClick() {
    if (this.RowTitleLink()) {
      this.generalService.router.navigateByUrl(this.RowTitleLink()!);
    }
  }

  nextSlide() {
    this.desktopSlider()?.next();
    // this.desktopSlider()?.moveToIdx(this.currentSlide + this.desktopSlider()?.track.details.maxIdx);
  }

  prevSlide() {
    this.desktopSlider()?.prev();
    // this.desktopSlider()?.moveToIdx(this.currentSlide - this.desktopSlider()?.track.details.maxIdx);
  }
}
